import React, { useState } from 'react';
import { Box, Button, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Trash } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { OnboardingAction, OnboardingState } from '../onboarding.types';
import { IRegisterUserRequest } from '../../../apis/models/auth';

interface Step5Props {
  state: OnboardingState;
  dispatch: React.Dispatch<OnboardingAction>;
}

const empty = (): IRegisterUserRequest => ({
  firstName: '',
  lastName: '',
  email: '',
  cellNumber: '',
});

const Step5_AdminAccounts: React.FC<Step5Props> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<IRegisterUserRequest>(empty());
  const [errors, setErrors] = useState<Partial<IRegisterUserRequest>>({});

  const validate = (): boolean => {
    const e: Partial<IRegisterUserRequest> = {};
    if (!form.firstName.trim()) e.firstName = t('validations_msg.required');
    if (!form.lastName.trim()) e.lastName = t('validations_msg.required');
    if (!form.email.trim()) e.email = t('validations_msg.required');
    if (!form.cellNumber.trim()) e.cellNumber = t('validations_msg.required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAdd = () => {
    if (!validate()) return;
    dispatch({ type: 'SET_ADMINS', payload: [...state.admins, { ...form }] });
    setForm(empty());
    setErrors({});
  };

  const handleRemove = (index: number) => {
    dispatch({ type: 'SET_ADMINS', payload: state.admins.filter((_, i) => i !== index) });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding.admins_hint')}
      </Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('onboarding.admin_first_name')}
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('onboarding.admin_last_name')}
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="email"
            label={t('onboarding.admin_email')}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('onboarding.admin_phone')}
            value={form.cellNumber}
            onChange={(e) => setForm({ ...form, cellNumber: e.target.value })}
            error={Boolean(errors.cellNumber)}
            helperText={errors.cellNumber}
          />
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={handleAdd}>
            {t('onboarding.btn_add')}
          </Button>
        </Grid>
      </Grid>

      {state.admins.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('onboarding.no_admins')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {state.admins.map((admin, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Box>
                <Typography variant="body1">
                  {admin.firstName} {admin.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {admin.email} · {admin.cellNumber}
                </Typography>
              </Box>
              <IconButton size="small" color="error" onClick={() => handleRemove(i)}>
                <Trash size={16} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Step5_AdminAccounts;
